import React from "react";
import { useTranslation } from "react-i18next";

type CompareRow = {
  label: string;
  halper: string;
  acquire: string;
  other: string;
};

// Comparison panel shown in the Marketplace header ("Why Halper?").
export function ComparePanel() {
  const { t } = useTranslation();

  const columns: {
    key: keyof Omit<CompareRow, "label">;
    name: string;
    highlight?: boolean;
  }[] = [
    { key: "halper", name: "HALPER", highlight: true },
    { key: "acquire", name: "acquire.com" },
    { key: "other", name: t("home.compareOtherMarketplaces") },
  ];

  const rows: CompareRow[] = [
    {
      label: t("home.compareClosingFee"),
      halper: "5%",
      acquire: "6-8%",
      other: "2.5-15%",
    },
    {
      label: t("home.compareListingFee"),
      halper: t("home.compareFree"),
      acquire: "$25-$100",
      other: "$49-$599",
    },
  ];

  return (
    <div className="pt-4 pb-1">
      <div className="rounded-2xl border border-gray-100 bg-white/70 p-4 sm:p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-sm sm:text-base font-semibold text-gray-900 tracking-tight">
            {t("home.compareTitle")}
          </h2>
        </div>

        {/* Mobile: stacked per-metric cards (no horizontal scroll) */}
        <div className="flex flex-col gap-3 sm:hidden">
          {rows.map((r) => (
            <div
              key={r.label}
              className="rounded-xl border border-gray-100 bg-gray-50/60 p-3"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                {r.label}
              </p>
              <div className="flex flex-col gap-1.5">
                {columns.map((c) => (
                  <div
                    key={c.key}
                    className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${
                      c.highlight
                        ? "bg-[#020637] text-white"
                        : "bg-white border border-gray-100 text-gray-700"
                    }`}
                  >
                    <span className="font-medium">{c.name}</span>
                    <span className="font-bold">{r[c.key]}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: comparison table */}
        <div className="hidden sm:block overflow-x-auto -mx-1 px-1">
          <div className="min-w-[640px] grid grid-cols-4 gap-2.5 text-center">
            <div />
            <div className="rounded-xl bg-[#020637] text-white text-sm font-bold py-3 shadow-lg shadow-[#020637]/10">
              HALPER
            </div>
            <div className="rounded-xl bg-gray-50 text-gray-900 text-sm font-semibold py-3 border border-gray-100">
              acquire.com
            </div>
            <div className="rounded-xl bg-gray-50 text-gray-900 text-sm font-semibold py-3 border border-gray-100">
              {t("home.compareOtherMarketplaces")}
            </div>

            {rows.map((r) => (
              <React.Fragment key={r.label}>
                <div className="rounded-xl bg-gray-50 text-gray-800 text-sm font-semibold py-3 px-4 flex items-center border border-gray-100">
                  {r.label}
                </div>
                <div className="rounded-xl bg-[#020637]/[0.04] text-[#020637] text-sm font-bold py-3 flex items-center justify-center border border-[#020637]/15">
                  {r.halper}
                </div>
                <div className="rounded-xl bg-white text-gray-600 text-sm font-medium py-3 flex items-center justify-center border border-gray-100">
                  {r.acquire}
                </div>
                <div className="rounded-xl bg-white text-gray-600 text-sm font-medium py-3 flex items-center justify-center border border-gray-100">
                  {r.other}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ComparePanel;
